import React, { useState } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Row, 
  Col,
  Typography,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  DatePicker,
  Alert,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  UserOutlined,
  PhoneOutlined,
  CarOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Driver {
  id: string;
  user_id?: string;
  license_number: string;
  license_expiry?: string;
  license_categories?: string[];
  date_of_birth?: string;
  address?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  hire_date?: string;
  is_active: boolean;
  assigned_vehicle_id?: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  vehicle_license_plate?: string;
  vehicle_brand?: string;
  vehicle_model?: string;
}

interface DriverFormData {
  // User information (for new drivers)
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  
  // Driver-specific fields
  license_number: string;
  license_expiry?: string;
  license_categories?: string[];
  date_of_birth?: string;
  address?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  hire_date?: string;
  assigned_vehicle_id?: string;
}

const AdminDrivers: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Récupération des chauffeurs
  const { data: driversData, isLoading } = useQuery({
    queryKey: ['drivers', searchText],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);

      const response = await apiClient.get(`/admin/drivers?${params.toString()}`);
      return response.data;
    },
  });

  // Récupération des véhicules disponibles pour assignation
  const { data: vehiclesData } = useQuery({
    queryKey: ['available-vehicles'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/vehicles?status=available');
      return response.data;
    },
  });

  const drivers: Driver[] = driversData?.drivers || [];
  const availableVehicles = vehiclesData?.vehicles || [];

  // Mutations
  const createDriverMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiClient.post('/admin/drivers', data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Chauffeur créé avec succès');
      setIsModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.details?.[0]?.message ||
                          error.response?.data?.error ||
                          error.message;
      message.error(`Erreur lors de la création: ${errorMessage}`);
    },
  });

  const updateDriverMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DriverFormData> }) => {
      const response = await apiClient.put(`/admin/drivers/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Chauffeur mis à jour avec succès');
      setIsModalVisible(false);
      setEditingDriver(null);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.details?.[0]?.message ||
                          error.response?.data?.error ||
                          error.message;
      message.error(`Erreur lors de la mise à jour: ${errorMessage}`);
    },
  });

  const deleteDriverMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/drivers/${id}`);
    },
    onSuccess: () => {
      message.success('Chauffeur supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
    onError: (error: any) => {
      message.error(`Erreur lors de la suppression: ${error.message}`);
    },
  });

  // Handlers
  const handleCreate = () => {
    setEditingDriver(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (driver: Driver) => {
    setEditingDriver(driver);
    form.setFieldsValue({
      ...driver,
      license_expiry: driver.license_expiry ? dayjs(driver.license_expiry) : null,
      date_of_birth: driver.date_of_birth ? dayjs(driver.date_of_birth) : null,
      hire_date: driver.hire_date ? dayjs(driver.hire_date) : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteDriverMutation.mutate(id);
  };

  const handleSubmit = (values: any) => {
    const formattedData = {
      ...values,
      license_expiry: values.license_expiry ? values.license_expiry.format('YYYY-MM-DD') : null,
      date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
      hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null,
    };

    if (editingDriver) {
      // For updates, send driver data directly (without user_data)
      const updateData = {
        license_number: formattedData.license_number,
        license_expiry: formattedData.license_expiry,
        license_categories: formattedData.license_categories,
        date_of_birth: formattedData.date_of_birth,
        address: formattedData.address,
        emergency_contact_name: formattedData.emergency_contact_name,
        emergency_contact_phone: formattedData.emergency_contact_phone,
        hire_date: formattedData.hire_date,
        assigned_vehicle_id: formattedData.assigned_vehicle_id,
      };
      updateDriverMutation.mutate({ id: editingDriver.id, data: updateData });
    } else {
      // For creation, wrap user fields in user_data
      const createData = {
        user_data: {
          first_name: formattedData.first_name,
          last_name: formattedData.last_name,
          email: formattedData.email,
          phone: formattedData.phone,
        },
        license_number: formattedData.license_number,
        license_expiry: formattedData.license_expiry,
        license_categories: formattedData.license_categories,
        date_of_birth: formattedData.date_of_birth,
        address: formattedData.address,
        emergency_contact_name: formattedData.emergency_contact_name,
        emergency_contact_phone: formattedData.emergency_contact_phone,
        hire_date: formattedData.hire_date,
        assigned_vehicle_id: formattedData.assigned_vehicle_id,
      };
      createDriverMutation.mutate(createData);
    }
  };

  // Fonction pour vérifier si le permis expire bientôt
  const isLicenseExpiringSoon = (driver: Driver) => {
    if (!driver.license_expiry) return false;
    return dayjs(driver.license_expiry).isBefore(dayjs().add(30, 'days'));
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Chauffeur',
      key: 'driver',
      render: (record: Driver) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <UserOutlined style={{ marginRight: '8px' }} />
            {record.first_name || record.last_name ? 
              `${record.first_name || ''} ${record.last_name || ''}`.trim() : 
              'Nom non renseigné'
            }
            {isLicenseExpiringSoon(record) && (
              <Tooltip title="Permis expire bientôt">
                <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: '8px' }} />
              </Tooltip>
            )}
          </div>
          {record.email && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.email}
            </Text>
          )}
          <div style={{ fontSize: '12px', color: '#999' }}>
            Permis: {record.license_number}
          </div>
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active: boolean) => (
        <Tag color={is_active ? 'green' : 'red'}>
          {is_active ? 'Actif' : 'Inactif'}
        </Tag>
      ),
      filters: [
        { text: 'Actif', value: true },
        { text: 'Inactif', value: false },
      ],
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record: Driver) => (
        <div>
          {record.phone && (
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>
              <PhoneOutlined style={{ marginRight: '4px' }} />
              {record.phone}
            </div>
          )}
          {record.emergency_contact_name && (
            <div style={{ fontSize: '12px' }}>
              <Text type="secondary">Urgence: </Text>
              <Text>{record.emergency_contact_name}</Text>
              {record.emergency_contact_phone && (
                <div style={{ fontSize: '11px', color: '#999' }}>
                  {record.emergency_contact_phone}
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Permis',
      key: 'license',
      render: (record: Driver) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <Text>N°: {record.license_number}</Text>
          </div>
          {record.license_categories && record.license_categories.length > 0 && (
            <div style={{ marginTop: '4px' }}>
              {record.license_categories.map(category => (
                <Tag key={category} color="blue">
                  {category}
                </Tag>
              ))}
            </div>
          )}
          {record.license_expiry && (
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              <Text type="secondary">Expire le: </Text>
              <Text style={{ 
                color: isLicenseExpiringSoon(record) ? '#faad14' : 'inherit',
                fontWeight: isLicenseExpiringSoon(record) ? 'bold' : 'normal'
              }}>
                {dayjs(record.license_expiry).format('DD/MM/YYYY')}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Véhicule Assigné',
      key: 'vehicle',
      render: (record: Driver) => (
        record.vehicle_license_plate ? (
          <div>
            <Badge status="success" />
            <div style={{ fontWeight: 'bold' }}>
              <CarOutlined style={{ marginRight: '4px' }} />
              {record.vehicle_license_plate}
            </div>
            {(record.vehicle_brand || record.vehicle_model) && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {record.vehicle_brand} {record.vehicle_model}
              </div>
            )}
          </div>
        ) : (
          <Text type="secondary">
            <Badge status="default" />
            Aucun véhicule
          </Text>
        )
      ),
    },
    {
      title: 'Embauche',
      dataIndex: 'hire_date',
      key: 'hire_date',
      render: (date: string) => (
        date ? dayjs(date).format('DD/MM/YYYY') : <Text type="secondary">Non renseigné</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Driver) => (
        <Space>
          <Tooltip title="Modifier">
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer ce chauffeur ?"
              description="Cette action est irréversible."
              onConfirm={() => handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const licenseAlerts = drivers.filter(isLicenseExpiringSoon);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Gestion des Chauffeurs</Title>
        <p style={{ color: '#666', margin: 0 }}>
          Gérez vos chauffeurs et leurs informations
        </p>
      </div>

      {/* Alertes de permis expirant */}
      {licenseAlerts.length > 0 && (
        <Alert
          message={`${licenseAlerts.length} permis expire(nt) dans les 30 prochains jours`}
          description={
            <div>
              {licenseAlerts.map(driver => (
                <Tag key={driver.id} color="orange" style={{ margin: '2px' }}>
                  {driver.first_name} {driver.last_name} - {dayjs(driver.license_expiry).format('DD/MM/YYYY')}
                </Tag>
              ))}
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Filtres et actions */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12}>
            <Input
              placeholder="Rechercher par nom, permis..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouveau chauffeur
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau des chauffeurs */}
      <Card>
        <Table
          columns={columns}
          dataSource={drivers}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} sur ${total} chauffeurs`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Modal de création/modification */}
      <Modal
        title={editingDriver ? 'Modifier le chauffeur' : 'Nouveau chauffeur'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingDriver(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* USER INFORMATION SECTION - Only for new drivers */}
          {!editingDriver && (
            <>
              <Alert
                message="Informations utilisateur"
                description="Un compte utilisateur sera créé automatiquement pour ce chauffeur avec un mot de passe généré."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="first_name"
                    label="Prénom"
                    rules={[{ required: true, message: 'Le prénom est requis' }]}
                  >
                    <Input placeholder="Prénom du chauffeur" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="last_name"
                    label="Nom"
                    rules={[{ required: true, message: 'Le nom est requis' }]}
                  >
                    <Input placeholder="Nom du chauffeur" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: "L'email est requis" },
                      { type: 'email', message: "Format d'email invalide" }
                    ]}
                  >
                    <Input placeholder="email@exemple.com" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label="Téléphone"
                    rules={[{ required: true, message: 'Le téléphone est requis' }]}
                  >
                    <Input placeholder="+33123456789" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Informations du permis de conduire</Divider>
            </>
          )}

          {/* LICENSE INFORMATION */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="license_number"
                label="Numéro de permis"
                rules={[{ required: true, message: 'Le numéro de permis est requis' }]}
              >
                <Input placeholder="Ex: 123456789" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="license_expiry"
                label="Date d'expiration du permis"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Sélectionner une date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="license_categories"
                label="Catégories de permis"
              >
                <Select
                  mode="multiple"
                  placeholder="Sélectionner les catégories"
                  options={[
                    { label: 'B', value: 'B' },
                    { label: 'C', value: 'C' },
                    { label: 'C1', value: 'C1' },
                    { label: 'CE', value: 'CE' },
                    { label: 'C1E', value: 'C1E' },
                    { label: 'D', value: 'D' },
                    { label: 'D1', value: 'D1' },
                    { label: 'DE', value: 'DE' },
                    { label: 'D1E', value: 'D1E' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="date_of_birth"
                label="Date de naissance"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Sélectionner une date"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* PERSONAL INFORMATION */}
          <Form.Item
            name="address"
            label="Adresse"
          >
            <Input.TextArea 
              rows={2}
              placeholder="Adresse complète du chauffeur"
            />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="emergency_contact_name"
                label="Contact d'urgence (nom)"
              >
                <Input placeholder="Nom du contact d'urgence" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="emergency_contact_phone"
                label="Contact d'urgence (téléphone)"
              >
                <Input placeholder="Numéro de téléphone" />
              </Form.Item>
            </Col>
          </Row>

          {/* EMPLOYMENT INFORMATION */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="hire_date"
                label="Date d'embauche"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Sélectionner une date"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="assigned_vehicle_id"
                label="Véhicule assigné"
              >
                <Select
                  placeholder="Sélectionner un véhicule"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {availableVehicles.map((vehicle: any) => (
                    <Option key={vehicle.id} value={vehicle.id}>
                      {vehicle.license_plate} - {vehicle.brand} {vehicle.model}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" style={{ marginTop: '24px' }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false);
                setEditingDriver(null);
                form.resetFields();
              }}>
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createDriverMutation.isPending || updateDriverMutation.isPending}
              >
                {editingDriver ? 'Mettre à jour' : 'Créer'}
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminDrivers;